<template>
    <UModal
        v-model:open="isOpen"
        :ui="{ content: 'mx-4 w-full md:w-3/5 lg:w-1/2 max-w-2xl overflow-hidden p-0 gap-0' }"
    >
        <template #content>
            <div class="flex flex-col bg-(--ui-bg-elevated) max-h-[82vh]">
                <!-- Search Input Header -->
                <div class="p-3.5 border-b border-default flex items-center gap-3">
                    <UIcon
                        :name="loading ? 'i-lucide-loader-2' : 'i-lucide-search'"
                        class="size-5 text-primary shrink-0"
                        :class="{ 'animate-spin': loading }"
                    />
                    <input
                        ref="inputRef"
                        v-model="query"
                        type="text"
                        :placeholder="t('search.input_placeholder')"
                        class="flex-1 bg-transparent text-sm sm:text-base text-highlighted placeholder:text-muted focus:outline-none"
                        autocomplete="off"
                        spellcheck="false"
                        @keydown="onKeyDown"
                    >
                    <button
                        v-if="query"
                        type="button"
                        class="text-muted hover:text-highlighted p-1 rounded transition-colors cursor-pointer"
                        :title="t('general.clear') || 'Clear'"
                        @click="clearQuery"
                    >
                        <UIcon name="i-lucide-x" class="size-4" />
                    </button>
                    <div class="hidden sm:flex items-center gap-1 shrink-0">
                        <UKbd size="xs" variant="subtle">ESC</UKbd>
                    </div>
                </div>

                <!-- Filter Tabs / Categories -->
                <div class="px-3 py-2 border-b border-default/60 bg-(--ui-bg-elevated)/60 flex items-center gap-1.5 overflow-x-auto scrollbar-none text-xs">
                    <button
                        v-for="cat in categories"
                        :key="cat.id"
                        type="button"
                        class="px-2.5 py-1 rounded-md font-medium transition-all shrink-0 cursor-pointer flex items-center gap-1.5"
                        :class="activeCategory === cat.id
                            ? 'bg-primary text-inverted'
                            : 'text-muted hover:text-highlighted hover:bg-elevated'"
                        @click="activeCategory = cat.id"
                    >
                        <UIcon :name="cat.icon" class="size-3.5" />
                        <span>{{ cat.label }}</span>
                        <span
                            v-if="cat.count !== undefined"
                            class="px-1.5 py-0.2 rounded-full text-[10px] font-mono"
                            :class="activeCategory === cat.id ? 'bg-black/20 text-inverted' : 'bg-default text-muted'"
                        >
                            {{ cat.count }}
                        </span>
                    </button>
                </div>

                <!-- Results or Quick Nav Body -->
                <div class="overflow-y-auto p-2 space-y-1 divide-y divide-default/40">
                    <!-- Loading Skeleton -->
                    <div v-if="loading && flatItems.length === 0" class="p-4 space-y-3">
                        <div v-for="i in 4" :key="i" class="flex items-center gap-3">
                            <USkeleton class="size-8 rounded-lg shrink-0" />
                            <div class="space-y-1.5 flex-1">
                                <USkeleton class="h-3.5 w-1/3 rounded" />
                                <USkeleton class="h-3 w-2/3 rounded" />
                            </div>
                        </div>
                    </div>

                    <!-- Empty state: No Results for Query -->
                    <div
                        v-else-if="query.trim() && flatItems.length === 0 && !loading"
                        class="p-8 text-center space-y-2 text-muted"
                    >
                        <UIcon name="i-lucide-search-x" class="size-8 mx-auto text-dimmed" />
                        <p class="text-sm font-medium text-highlighted">
                            {{ t('search.no_results_title') }} "<span class="font-semibold text-primary">{{ query }}</span>"
                        </p>
                        <p class="text-xs text-muted max-w-sm mx-auto">
                            {{ t('search.no_results_desc') }}
                        </p>
                    </div>

                    <!-- Quick Navigation (Query is empty) -->
                    <div v-else-if="!query.trim()" class="p-2 space-y-3">
                        <div class="px-2 pt-1 text-[11px] font-mono font-bold uppercase tracking-wider text-muted">
                            {{ t('search.quick_navigation') }}
                        </div>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            <button
                                v-for="(item, idx) in quickNavItems"
                                :key="item.route"
                                type="button"
                                class="flex items-center gap-2.5 p-2 rounded-lg border border-default/50 hover:border-primary/50 hover:bg-primary/5 transition-all text-left cursor-pointer group"
                                :class="{ 'ring-2 ring-primary/60 bg-primary/10 border-primary/60': focusedIndex === idx }"
                                @click="selectItem(item)"
                                @mouseenter="focusedIndex = idx"
                            >
                                <div class="p-2 rounded-md bg-(--ui-bg-elevated) border border-default group-hover:border-primary/40 text-primary shrink-0">
                                    <UIcon :name="item.icon || 'i-lucide-arrow-right'" class="size-4" />
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="text-xs font-semibold text-highlighted truncate group-hover:text-primary transition-colors">
                                        {{ item.title }}
                                    </div>
                                    <div class="text-[11px] text-muted truncate">
                                        {{ item.subtitle }}
                                    </div>
                                </div>
                                <UIcon name="i-lucide-chevron-right" class="size-3.5 text-dimmed group-hover:text-primary transition-colors shrink-0" />
                            </button>
                        </div>
                    </div>

                    <!-- Search Results List -->
                    <div v-else class="space-y-1 pt-1">
                        <button
                            v-for="(item, idx) in flatItems"
                            :key="item.id + item.type + idx"
                            type="button"
                            class="w-full flex items-center justify-between gap-3 p-2.5 rounded-lg border border-transparent hover:border-primary/40 hover:bg-primary/5 transition-all text-left cursor-pointer group"
                            :class="{ 'bg-primary/10 border-primary/60 shadow-2xs': focusedIndex === idx }"
                            @click="selectItem(item)"
                            @mouseenter="focusedIndex = idx"
                        >
                            <div class="flex items-center gap-3 min-w-0 flex-1">
                                <!-- Type Icon -->
                                <div
                                    class="p-2 rounded-lg shrink-0 border"
                                    :class="itemTypeBadgeStyle(item.type)"
                                >
                                    <UIcon :name="item.icon || 'i-lucide-file'" class="size-4" />
                                </div>

                                <!-- Text Details -->
                                <div class="min-w-0 flex-1">
                                    <div class="flex items-center gap-2">
                                        <span class="text-xs font-semibold text-highlighted truncate group-hover:text-primary transition-colors">
                                            {{ item.title }}
                                        </span>
                                        <!-- Status Indicator Dot -->
                                        <span
                                            v-if="item.status"
                                            class="size-1.5 rounded-full shrink-0"
                                            :class="getStatusDotClass(item.status)"
                                            :title="item.status"
                                        />
                                        <!-- Value pill (if present, e.g. for sensors) -->
                                        <span
                                            v-if="item.value"
                                            class="px-1.5 py-0.2 rounded text-[10px] font-mono font-medium bg-(--ui-bg-elevated) border border-default text-foreground shrink-0"
                                        >
                                            {{ item.value }}
                                        </span>
                                    </div>
                                    <div class="text-[11px] text-muted truncate mt-0.5">
                                        {{ item.subtitle }}
                                    </div>
                                </div>
                            </div>

                            <!-- Right Badge & Action hint -->
                            <div class="flex items-center gap-2 shrink-0">
                                <span class="px-2 py-0.5 rounded text-[10px] font-mono uppercase font-bold tracking-wider bg-(--ui-bg-elevated) border border-default text-muted">
                                    {{ item.badge || item.type }}
                                </span>
                                <UIcon
                                    name="i-lucide-corner-down-left"
                                    class="size-3.5 text-dimmed group-hover:text-primary transition-colors"
                                />
                            </div>
                        </button>
                    </div>
                </div>

                <!-- Footer Hints -->
                <div class="p-2.5 border-t border-default/60 bg-(--ui-bg-elevated)/80 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted">
                    <div class="flex items-center gap-3">
                        <span class="flex items-center gap-1">
                            <UKbd size="xs" variant="subtle">↑</UKbd>
                            <UKbd size="xs" variant="subtle">↓</UKbd>
                            <span>{{ t('search.hint_navigate') }}</span>
                        </span>
                        <span class="flex items-center gap-1">
                            <UKbd size="xs" variant="subtle">↵</UKbd>
                            <span>{{ t('search.hint_open') }}</span>
                        </span>
                    </div>
                    <div class="flex items-center gap-1 text-[10px] text-dimmed">
                        <span>NervusEye MONITOR Search</span>
                    </div>
                </div>
            </div>
        </template>
    </UModal>
</template>

<script lang="ts" setup>
    import { computed, nextTick, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRouter } from 'vue-router'

    import { getStatusDotClass } from '@/libs/status'
    import { useFetchStore } from '@/stores/fetch'
    import type { GlobalSearchResponse, SearchResultItem } from '@/types/Search'

    const { t } = useI18n()
    const router = useRouter()
    const fetchStore = useFetchStore()

    const isOpen = ref(false)
    const query = ref('')
    const loading = ref(false)
    const focusedIndex = ref(0)
    const activeCategory = ref<'all' | 'devices' | 'sensors' | 'ipam' | 'subnets' | 'navigation'>('all')

    const inputRef = ref<HTMLInputElement | null>(null)

    const searchResponse = ref<GlobalSearchResponse>({
        query:      '',
        total:      0,
        devices:    [],
        sensors:    [],
        ipam_hosts: [],
        subnets:    [],
    })

    let searchTimer: ReturnType<typeof setTimeout> | null = null

    const quickNavItems = computed<SearchResultItem[]>(() => [
        {
            id:       'nav-dashboard',
            type:     'action',
            title:    t('nav.dashboard'),
            subtitle: 'Real-time telemetry and KPI leaderboards',
            route:    '/dashboard',
            icon:     'i-lucide-layout-dashboard',
            badge:    'Page',
        },
        {
            id:       'nav-devices',
            type:     'action',
            title:    t('nav.devices'),
            subtitle: 'Device tree, physical ports and sensor metrics',
            route:    '/devices',
            icon:     'i-lucide-server',
            badge:    'Page',
        },
        {
            id:       'nav-topology',
            type:     'action',
            title:    t('nav.topology'),
            subtitle: 'Live L2/L3 topology & STP link visualizer',
            route:    '/topology',
            icon:     'i-lucide-network',
            badge:    'Page',
        },
        {
            id:       'nav-ipam',
            type:     'action',
            title:    t('nav.ipam'),
            subtitle: 'Subnet plans, VLANs, trunks & DHCP leases',
            route:    '/ipam',
            icon:     'i-lucide-binary',
            badge:    'Page',
        },
        {
            id:       'nav-syslog',
            type:     'action',
            title:    t('nav.syslog'),
            subtitle: 'Live syslog receiver, traps & config diffs',
            route:    '/syslog',
            icon:     'i-lucide-terminal',
            badge:    'Page',
        },
        {
            id:       'nav-security',
            type:     'action',
            title:    t('nav.security'),
            subtitle: 'Port security and rogue device detection',
            route:    '/security',
            icon:     'i-lucide-shield-alert',
            badge:    'Page',
        },
        {
            id:       'nav-sla',
            type:     'action',
            title:    t('nav.sla') || 'SLA Reports',
            subtitle: 'Contractual SLA reports, uptime & MTTR',
            route:    '/reports/sla',
            icon:     'i-lucide-award',
            badge:    'Report',
        },
        {
            id:       'nav-tools',
            type:     'action',
            title:    t('nav.tools') || 'Network Tools',
            subtitle: 'Ping, Traceroute, DNS, PortScan, SPF/DKIM, BGP & Looking Glass',
            route:    '/tools',
            icon:     'i-lucide-wrench',
            badge:    'Tools',
        },
        {
            id:       'nav-settings',
            type:     'action',
            title:    t('nav.settings'),
            subtitle: 'System settings, users, audit and backups',
            route:    '/settings',
            icon:     'i-lucide-settings',
            badge:    'Page',
        },
        {
            id:       'nav-about',
            type:     'action',
            title:    t('nav.about'),
            subtitle: 'System documentation, architecture and help',
            route:    '/about?tab=help',
            icon:     'i-lucide-help-circle',
            badge:    'Help',
        },
    ])

    const categories = computed(() => {
        const devCount = searchResponse.value.devices.length
        const sensorCount = searchResponse.value.sensors.length
        const ipamCount = searchResponse.value.ipam_hosts.length
        const subnetCount = searchResponse.value.subnets.length

        return [
            {
                id:    'all' as const,
                label: t('search.category_all') || 'All',
                icon:  'i-lucide-layers',
                count: query.value.trim() ? searchResponse.value.total : undefined,
            },
            {
                id:    'devices' as const,
                label: t('search.category_devices') || 'Devices',
                icon:  'i-lucide-server',
                count: query.value.trim() ? devCount : undefined,
            },
            {
                id:    'sensors' as const,
                label: t('search.category_sensors') || 'Sensors',
                icon:  'i-lucide-activity',
                count: query.value.trim() ? sensorCount : undefined,
            },
            {
                id:    'ipam' as const,
                label: t('search.category_hosts') || 'IPAM Hosts',
                icon:  'i-lucide-binary',
                count: query.value.trim() ? ipamCount : undefined,
            },
            {
                id:    'subnets' as const,
                label: t('search.category_subnets') || 'Subnets',
                icon:  'i-lucide-network',
                count: query.value.trim() ? subnetCount : undefined,
            },
            {
                id:    'navigation' as const,
                label: t('search.category_navigation') || 'Navigation',
                icon:  'i-lucide-compass',
                count: !query.value.trim() ? quickNavItems.value.length : undefined,
            },
        ]
    })

    const flatItems = computed<SearchResultItem[]>(() => {
        if (!query.value.trim()) {
            return activeCategory.value === 'all' || activeCategory.value === 'navigation'
                ? quickNavItems.value
                : []
        }

        const items: SearchResultItem[] = []

        if (activeCategory.value === 'all' || activeCategory.value === 'devices') {
            items.push(...searchResponse.value.devices)
        }

        if (activeCategory.value === 'all' || activeCategory.value === 'sensors') {
            items.push(...searchResponse.value.sensors)
        }

        if (activeCategory.value === 'all' || activeCategory.value === 'ipam') {
            items.push(...searchResponse.value.ipam_hosts)
        }

        if (activeCategory.value === 'all' || activeCategory.value === 'subnets') {
            items.push(...searchResponse.value.subnets)
        }

        if (activeCategory.value === 'navigation') {
            const q = query.value.toLowerCase().trim()
            items.push(...quickNavItems.value.filter((i) => i.title.toLowerCase().includes(q) || i.subtitle.toLowerCase().includes(q)))
        }

        return items
    })

    const itemTypeBadgeStyle = (type: string) => {
        switch (type) {
            case 'device':
                return 'bg-info/10 text-info border-info/20'
            case 'sensor':
                return 'bg-success/10 text-success border-success/20'
            case 'ipam_host':
                return 'bg-primary/10 text-primary border-primary/20'
            case 'subnet':
                return 'bg-warning/10 text-warning border-warning/20'
            case 'action':
                return 'bg-elevated text-highlighted border-default'
            default:
                return 'bg-elevated text-muted border-default'
        }
    }

    const performSearch = async () => {
        const q = query.value.trim()
        if (!q) {
            searchResponse.value = {
                query:      '',
                total:      0,
                devices:    [],
                sensors:    [],
                ipam_hosts: [],
                subnets:    [],
            }
            loading.value = false
            focusedIndex.value = 0
            return
        }

        loading.value = true
        try {
            const { response, data } = await fetchStore
                .fetch(`/api/v1/search?q=${encodeURIComponent(q)}`)
                .get()
                .json<GlobalSearchResponse>()

            if (response.value?.status === 200 && data.value) {
                searchResponse.value = data.value
                focusedIndex.value = 0
            }
        } finally {
            loading.value = false
        }
    }

    watch(query, () => {
        if (searchTimer) clearTimeout(searchTimer)
        searchTimer = setTimeout(() => {
            void performSearch()
        }, 150)
    })

    const onKeyDown = (e: KeyboardEvent) => {
        const list = flatItems.value
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (list.length > 0) {
                focusedIndex.value = (focusedIndex.value + 1) % list.length
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            if (list.length > 0) {
                focusedIndex.value = (focusedIndex.value - 1 + list.length) % list.length
            }
        } else if (e.key === 'Enter') {
            e.preventDefault()
            if (list.length > 0 && list[focusedIndex.value]) {
                selectItem(list[focusedIndex.value])
            }
        } else if (e.key === 'Escape') {
            isOpen.value = false
        }
    }

    const clearQuery = () => {
        query.value = ''
        if (inputRef.value) {
            inputRef.value.focus()
        }
    }

    const selectItem = (item: SearchResultItem) => {
        isOpen.value = false
        if (!item.route) return

        void router.push(item.route)

        // Notify active views (such as Devices or IPAM) to update selection immediately
        if (item.type === 'device' || item.type === 'sensor') {
            window.dispatchEvent(
                new CustomEvent('nervuseye:select-node', {
                    detail: { id: item.type === 'device' ? `device-${item.id}` : `sensor-${item.id}` },
                })
            )
        } else if (item.type === 'ipam_host') {
            window.dispatchEvent(
                new CustomEvent('nervuseye:search-ipam', {
                    detail: { search: item.ip || item.title },
                })
            )
        }
    }

    const open = (initialQuery?: string) => {
        query.value = initialQuery || ''
        isOpen.value = true
        focusedIndex.value = 0
        activeCategory.value = 'all'

        if (query.value.trim()) {
            void performSearch()
        }

        void nextTick(() => {
            if (inputRef.value) {
                inputRef.value.focus()
                inputRef.value.select()
            }
        })
    }

    const close = () => {
        isOpen.value = false
    }

    defineExpose({
        open,
        close,
    })
</script>
